import { Trash2, Search } from "lucide-react";
import { LEAD_STATUSES } from "../../hooks/useLeads";

/** Filter row: search input + status dropdown + refresh button */
export function LeadsFilterBar({ q, setQ, status, setStatus, onRefresh }) {
  return (
    <div className="mt-6 flex flex-wrap gap-3 items-center">
      <div className="flex items-center gap-2 bg-white rounded-xl border border-black/10 px-3 py-2 w-full max-w-sm">
        <Search size={16} className="text-brand-ink2" />
        <input
          placeholder="Search by name, phone, concern…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onRefresh()}
          data-testid="leads-search"
          className="bg-transparent outline-none flex-1 text-sm"
        />
      </div>
      <select
        value={status}
        onChange={(e) => setStatus(e.target.value)}
        data-testid="leads-status-filter"
        className="rounded-xl border border-black/10 px-3 py-2 bg-white text-sm"
      >
        <option value="all">All statuses</option>
        {LEAD_STATUSES.map((s) => (
          <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
        ))}
      </select>
      <button onClick={onRefresh} className="btn-outline !py-2 !px-4 text-sm">Refresh</button>
    </div>
  );
}

/** Sortable leads table */
export function LeadsTable({ leads, selected, onSelect, onRemove }) {
  return (
    <div className="card-soft overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-bg-alt text-brand-ink2 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Phone</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-brand-ink2 text-center">No leads yet.</td></tr>
            )}
            {leads.map((l) => (
              <tr
                key={l.id}
                className={`border-t border-black/5 cursor-pointer ${selected?.id === l.id ? "bg-brand-cream/40" : "hover:bg-brand-bg-alt"}`}
                onClick={() => onSelect(l)}
                data-testid={`lead-row-${l.id}`}
              >
                <td className="px-4 py-3 font-medium text-brand-ink">{l.name}</td>
                <td className="px-4 py-3 text-brand-ink2">{l.phone}</td>
                <td className="px-4 py-3 text-brand-ink2">{l.condition_category || "—"}</td>
                <td className="px-4 py-3">
                  <span className="text-xs bg-brand-cream text-brand-blue rounded-full px-2 py-1">
                    {(l.status || "new").replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-4 py-3 text-brand-ink2 text-xs">{l.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); onRemove(l.id); }}
                    className="text-brand-ink2 hover:text-brand-terracotta"
                    data-testid={`lead-delete-${l.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Right-side detail panel for the selected lead */
export function LeadDetailPanel({ lead, onUpdate }) {
  if (!lead) {
    return (
      <aside className="card-soft p-5">
        <div className="text-sm text-brand-ink2">Select a lead to view details.</div>
      </aside>
    );
  }
  return (
    <aside className="card-soft p-5">
      <div className="text-xs uppercase text-brand-terracotta tracking-widest">Lead</div>
      <h3 className="font-heading text-xl text-brand-ink mt-1">{lead.name}</h3>
      <div className="text-sm text-brand-ink2 mt-1">{lead.phone} · {lead.condition_category || "—"}</div>
      <div className="mt-3 text-sm"><strong>Concern:</strong> {lead.concern || "—"}</div>
      <div className="mt-1 text-sm"><strong>Preferred time:</strong> {lead.preferred_time || "—"}</div>
      <div className="mt-1 text-sm"><strong>Source:</strong> {lead.source_page || "—"} · UTM {lead.utm_source || "direct"}</div>

      <label className="text-xs uppercase tracking-widest text-brand-ink2 mt-5 block">Status</label>
      <select
        value={lead.status || "new"}
        onChange={(e) => onUpdate(lead.id, { status: e.target.value })}
        data-testid="lead-status-edit"
        className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 bg-white text-sm"
      >
        {LEAD_STATUSES.map((s) => (
          <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
        ))}
      </select>

      <label className="text-xs uppercase tracking-widest text-brand-ink2 mt-4 block">Notes</label>
      <textarea
        defaultValue={lead.notes || ""}
        key={lead.id}
        onBlur={(e) => onUpdate(lead.id, { notes: e.target.value })}
        data-testid="lead-notes"
        className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 bg-white text-sm min-h-[100px]"
      />
    </aside>
  );
}
